import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useSelector } from 'react-redux';
import { FormFieldsGenerator, TFormFieldsGeneratorProps } from 'src/components/FormFieldsGenerator';
import { GRID_MARKET_MODE } from 'src/constants/application';
import { ConfigType, ConfigurationOutput, useReadConfigurationQuery } from 'src/graphql';
import { useAuth } from 'src/hooks/useAuth';
import { useCurrentRefs } from 'src/hooks/useCurrentRefs';
import {
  selectActiveConfigurationUuid,
  selectAssets,
  selectConfiguration,
  selectReadOnly,
  selectSettingsData,
  selectUsedAssetsNames,
} from 'src/redux/configuration/configuration.selectors';
import { EFormVariant } from 'src/typings/base-types';
import { TAllFieldNames, TFieldsUnionWithValue } from 'src/utils/assetsFields/assetsFields.types';
import {
  getAllFieldTemplatesWithValuesForAsset,
  getAssetValues,
} from 'src/utils/assetsFields/fieldTemplatesWithValues';
import { updateFields } from 'src/utils/assetsFields/updateFields';
import { TValuesByFieldName } from 'src/utils/assetsFields/valuesByFieldName.types';
import { validateFields } from 'src/utils/fieldValidation';

import { assetTypeToValidator } from './fieldValidators';
import { TFormAssetsParamsProps } from './FormAssetsParams.types';

export const FormAssetsParams: React.FC<TFormAssetsParamsProps> = ({
  hasErrorsRef,
  formVariant,
  assetUuid,
  assetType,
  currentValues,
  onSubmit,
  onChange,
  isCustomPV,
  isCustomLoad,
  isEdit,
  forceAll = false,
  fieldProps,
  ...formFieldsGeneratorProps
}) => {
  const usedAssetsNames = useSelector(selectUsedAssetsNames);
  const assets = useSelector(selectAssets);
  const settingsData = useSelector(selectSettingsData);
  const configurationCharacteristic = useSelector(selectConfiguration);
  const readOnly = useSelector(selectReadOnly);
  const configUuid = useSelector(selectActiveConfigurationUuid);

  const [fields, setFields] = useState<TFormFieldsGeneratorProps['fields'] | null>(null);
  const [errors, setErrors] = useState<TFormFieldsGeneratorProps['errors']>(null);

  const resolvedAssetType = assetType || assets[assetUuid].type;

  const newAssetType = useRef(resolvedAssetType);
  const currentRefs = useCurrentRefs({
    configurationCharacteristic,
    currentValues,
    settingsData,
    usedAssetsNames,
  });
  const { isAdmin } = useAuth();

  const { data: configurationResponse } = useReadConfigurationQuery({
    fetchPolicy: 'cache-first',
    skip: !configUuid,
    variables: { uuid: configUuid! },
  });
  const configuration: ConfigurationOutput | null = useMemo(
    () => configurationResponse?.readConfiguration as ConfigurationOutput | null,
    [configurationResponse],
  );

  function handleValidation(fields: TFieldsUnionWithValue[]) {
    return validateFields({
      validators: assetTypeToValidator[resolvedAssetType]({
        usedAssetsNames,
        currentValues,
      }),
      fields,
    });
  }

  useEffect(() => {
    if (assetType) newAssetType.current = assetType;
  }, [assetType]);

  const handleChange: TFormFieldsGeneratorProps['onChange'] = ({ name, value }) => {
    if (!fields || !assetUuid || !resolvedAssetType) return;

    const payload = {
      settingsData,
      isLibrary: false,
      configType: configuration?.type,
      configurationCharacteristic,
      isAdmin,
    };
    let newFields;

    if (name === GRID_MARKET_MODE) {
      // This is special case where we completely switch a device
      const _value = value as TValuesByFieldName['GRID_MARKET_MODE'];
      newAssetType.current = _value === 'MarketMakerMode' ? 'MarketMaker' : 'InfiniteBus';

      newFields = getAllFieldTemplatesWithValuesForAsset({
        type: newAssetType.current,
        values: { ...getAssetValues(fields), [name]: _value },
        ...payload,
        configType: configuration?.type || ConfigType.Collaboration,
      });
    } else {
      newFields = updateFields({
        type: resolvedAssetType,
        fields,
        updatedField: { name: name as TAllFieldNames, value },
        ...payload,
        configType: configuration?.type || ConfigType.Collaboration,
      });
    }

    const { errors } = handleValidation(newFields);
    if (onChange) onChange({ fieldName: name, value });
    setFields(newFields);
    setErrors(errors);
  };

  const handleBlur: TFormFieldsGeneratorProps['onBlur'] = () => {
    if (!fields) return;
    const { errors } = handleValidation(fields);
    setErrors(errors);
  };

  const handleSubmit: TFormFieldsGeneratorProps['onSubmit'] = () => {
    if (!fields) return;

    const { errors } = handleValidation(fields);

    if (errors) {
      setErrors(errors);
    } else {
      onSubmit({ assetUuid, values: getAssetValues(fields), assetType: newAssetType.current });
    }
  };

  useEffect(
    function setUnfilteredFields() {
      const { currentValues } = currentRefs.current;
      let formFields = getAllFieldTemplatesWithValuesForAsset({
        type: resolvedAssetType,
        values: currentValues,
        configType: configuration?.type || ConfigType.Collaboration,
        advancedSettings: configuration?.communityAdvancedSettings || {},
      });

      if (isCustomPV && formFields.length) {
        // manipulating fields in case of custom PV
        formFields = formFields.map((f) => {
          if (f.name === 'profileMode' && f.type === 'enum') {
            return {
              ...f,
              // default selection: Local Generation Profile if no profile is selected
              // also checks for edit case where profile is already selected
              value: isEdit ? f.value : 5,
            };
          }
          return f;
        });
      }

      setFields(formFields);
    },
    [resolvedAssetType, currentRefs, isCustomPV, isCustomLoad, isEdit, forceAll, configuration],
  );
  const renderedFields = useMemo(() => {
    switch (formVariant) {
      case EFormVariant.OnlyAdvanced:
        return fields?.filter((f) => f.formView === EFormVariant.Advanced);
      case EFormVariant.Advanced:
        return fields?.filter(
          (f) => f.formView === EFormVariant.Advanced || f.formView === EFormVariant.Express,
        );
      default:
        return fields?.filter((f) => f.formView === formVariant);
    }
  }, [fields, formVariant]);

  useEffect(() => {
    if (hasErrorsRef) {
      hasErrorsRef.current = Boolean(errors);
    }
  }, [hasErrorsRef, errors]);

  if (!renderedFields || renderedFields.length == 0) return <div>All bill components disabled</div>;

  return (
    <FormFieldsGenerator
      fields={renderedFields}
      onChange={handleChange}
      onBlur={handleBlur}
      errors={errors}
      onSubmit={handleSubmit}
      focusField="name"
      readOnly={readOnly || !isEdit}
      formVariant={formVariant}
      fieldProps={fieldProps}
      {...formFieldsGeneratorProps}
    />
  );
};
